"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { useRouter, usePathname } from "next/navigation";
import { Badge, Button, IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { CalendarMonth as MeetingIcon } from "@mui/icons-material";
import { canViewMeetings } from "@/lib/admin/permissions";
import { adminNavigationStyles } from "./adminNavigationStyles";

export default function AdminMeetingButton() {
  const { data: session } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isActive = pathname === "/admin/meetings";

  const hasMeetingAccess = canViewMeetings(session);

  const { data: pendingPayoutData } = useQuery({
    queryKey: ["admin", "meeting", "pending-payout-count"],
    enabled:
      hasMeetingAccess &&
      !session?.impersonating &&
      pathname === "/admin/meetings",
    queryFn: async () => {
      const res = await fetch("/api/admin/meeting/payout-pending-count", {
        method: "GET",
        cache: "no-store",
      });
      if (!res.ok) {
        throw new Error("Failed to load pending payout count");
      }
      return res.json();
    },
  });

  const pendingPayoutCount =
    typeof pendingPayoutData?.count === "number" ? pendingPayoutData.count : 0;

  if (!hasMeetingAccess || session?.impersonating) return null;

  const navigateToMeetingAdmin = () => {
    const event = new CustomEvent("meetingNavigationRequest", {
      detail: { href: "/admin/meetings" },
      cancelable: true,
      bubbles: false,
    });
    if (document.dispatchEvent(event)) {
      router.push("/admin/meetings");
    }
  };

  return (
    <>
      {isMobile ? (
        <Tooltip
          title={
            pendingPayoutCount > 0
              ? `Meeting management (${pendingPayoutCount} payout${
                  pendingPayoutCount === 1 ? "" : "s"
                } need attention)`
              : "Meeting management"
          }
        >
          <IconButton
            color="inherit"
            onClick={navigateToMeetingAdmin}
            size="small"
            sx={{ ml: 1 }}
          >
            <Badge
              badgeContent={pendingPayoutCount}
              color="error"
              invisible={pendingPayoutCount === 0}
              max={99}
            >
              <MeetingIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="text"
          color="inherit"
          onClick={navigateToMeetingAdmin}
          startIcon={
            <Badge
              badgeContent={pendingPayoutCount}
              color="error"
              invisible={pendingPayoutCount === 0}
              max={99}
            >
              <MeetingIcon />
            </Badge>
          }
          aria-current={isActive ? "page" : undefined}
          sx={adminNavigationStyles(isActive)}
        >
          Meeting admin
        </Button>
      )}
    </>
  );
}
